<template>
  <rounded-card
    :head-text="$t('profile.edit_profile')"
    height="97%"
    show-right-btn
    @set-close-status="$emit('cancel')"
  >
    <div class="form-wrapper">
      <!-- Avatar -->
      <div class="avatar-wrapper">
        <avatar class="mx-auto mb-3" :src="avatarSrc" show-upload @click="selectFile" />
        <input
          ref="file"
          type="file"
          class="hidden"
          accept=".jpg, .jpeg, .png"
          @change="onFileSelected"
        />
        <div v-if="profile.email" class="mb-3">{{ profile.email }}</div>
      </div>
      <!-- Name -->
      <text-field
        class="w-full my-3"
        v-model.trim="local.nick_name"
        required
        :placeholder="$t('profile.name')"
        :error.sync="nameError"
      />
      <!--  Birthday -->
      <div class="w-full my-3 flex flex-row">
        <birth-year class="w-full" v-model="birthyear" :error.sync="birthyearError" />
        <gender-selector v-model="local.gender" />
      </div>
      <div class="width-height-wrapper">
        <!--  Height -->
        <height class="w-full my-3 md:w-1/2 md:mr-5" v-model="local.height" />
        <!--  Weight -->
        <weight class="w-full my-3 md:w-1/2 md:ml-5" v-model="local.weight" />
        <system-selector class="self-end mb-3" />
      </div>
      <!--  Phone -->
      <phone
        class="w-full my-3"
        v-model="localNumber"
        :country-code="countryCode"
        :disabled="auth.username === profile.mobile_phone"
        @edit-code="openCountryCode"
      />
      <!--  Email -->
      <email-field
        class="w-full my-3"
        :disabled="auth.username.toLowerCase() === profile.email.toLowerCase()"
        v-model.trim="local.email"
        :placeholder="$t('profile.email')"
        :error.sync="emailError"
      />
      <!--  Save -->
      <c-button class="my-auto md:mb-10" :confirmed-status="canSave" @click="onClick">
        <span>{{ $t('profile.save') }}</span>
      </c-button>
    </div>
    <country-code
      v-if="showCountryCode"
      :country-code.sync="countryCode"
      @set-close-status="closeCountryCode"
    />
  </rounded-card>
</template>

<script>
import handlePhoneInput from '@/mixins/handle-phone-input';
import showAvatar from '@/mixins/show-avatar';
import SystemSelector from '@/components/common/Fields/SystemSelector.vue';
import Phone from '@/components/common/Fields/Phone.vue';
import CountryCode from '@/components/DefaultCloudzet/Login/CountryCode.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import TextField from '@/components/common/Fields/Text.vue';
import EmailField from '@/components/common/Fields/Email.vue';
import BirthYear from '@/components/common/Fields/BirthYear.vue';
import Height from '@/components/common/Fields/Height.vue';
import Weight from '@/components/common/Fields/Weight.vue';
import GenderSelector from '@/components/common/Fields/GenderSelector.vue';
import CButton from '@/components/common/CButton.vue';
import Avatar from '@/components/common/Avatar.vue';

const FILE_SIZE = 1024 * 1024; // 1MB

const FIELDS = ['nick_name', 'gender'];

export default {
  name: 'EditProfile',
  mixins: [handlePhoneInput, showAvatar],
  components: {
    RoundedCard,
    TextField,
    SystemSelector,
    BirthYear,
    Height,
    Weight,
    GenderSelector,
    CButton,
    Avatar,
    CountryCode,
    Phone,
    EmailField,
  },
  props: {
    profile: Object,
    isEditing: Boolean,
    auth: Object,
  },
  data: () => ({
    local: {},
    nameError: false,
    emailError: false,
    birthyear: '',
    birthyearError: false,
  }),
  methods: {
    onClick() {
      if (this.canSave) {
        if (this.phoneNumber && this.localNumber) {
          this.local.mobile_phone = this.phoneNumber;
        }
        if (this.birthyear && !this.birthyearError) {
          this.local.birthday = `${this.birthyear}-01-01`;
        }
        this.$emit('save', this.local);
      }
    },
    checkValidEmail(email) {
      const emailRegExp = /^\w+((-\w+)|(\.\w+)|(\+\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/;
      return email.length ? email.length > 3 && emailRegExp.test(email) : true;
    },
    selectFile() {
      const $file = this.$refs.file;
      if (!$file) return;

      $file.click();
    },
    onFileSelected() {
      const $file = this.$refs.file;
      if (!$file || !$file.files || !$file.files[0]) return;

      const file = $file.files[0];
      if (file.size > FILE_SIZE) {
        this.$toasted.show(this.$t('profile.file_size_exceed'));
        return;
      }

      this.local.avatar = file;
      this.updatePreview(file);
    },
    updatePreview(file) {
      if (!FileReader) return;

      const reader = new FileReader();
      reader.onload = () => {
        this.newAvatarPreview = reader.result;
      };
      reader.onerror = () => {
        this.newAvatarPreview = '';
      };
      reader.readAsDataURL(file);
    },
  },
  computed: {
    canSave() {
      return FIELDS.every(f => !!this.local[f]) && !!this.birthyear && !this.birthyearError;
    },
  },
  watch: {
    isEditing(nv) {
      if (nv) {
        this.local = { ...this.profile };
        this.birthyear = parseInt(this.local.birthday, 10) || '';
        this.nameError = !this.local.nick_name;
        this.birthyearError = !this.birthyear;
        this.emailError = !this.checkValidEmail(this.local.email);
        this.countryCode = {};
        this.localNumber = '';
        this.setLocalPhone(this.local.mobile_phone);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-wrapper {
  @apply flex flex-col items-center w-full flex-1 px-8 max-w-sm;
  & > *:not(:first-child) {
    max-height: 45px;
  }
  .width-height-wrapper {
    @apply w-full flex flex-col;
    max-height: max-content;
  }
  @screen md {
    @apply max-w-2xl;

    .width-height-wrapper {
      @apply flex-row;
    }
  }
}
</style>
